import { useCallback, useEffect, useRef } from 'react';

import type {
	AiSuggestionsEntryPoint,
	AiSuggestionsEmptyStateType,
	AiSuggestionsEventPayload,
	AiSuggestionsRightRailEntryPoint,
} from '@atlaskit/editor-common/analytics';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

export const SUGGESTIONS_PANEL_ANALYTICS_SOURCE = 'suggestionsPanel';

export const SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT: AiSuggestionsEntryPoint =
	'suggestionsPanelEmptyState';

type SuggestionAnalyticsSnapshot = Pick<
	Suggestion,
	'action' | 'actionKind' | 'createdAt' | 'toolCalls' | 'type'
>;

export type FireAiSuggestionsAnalyticsEvent = (event: AiSuggestionsEventPayload) => void;

const getTimeSinceGenerationAttribute = (
	suggestion: Pick<Suggestion, 'createdAt'>,
): { timeSinceGenerationMs?: number } => {
	const createdAt = Date.parse(suggestion.createdAt);
	return Number.isFinite(createdAt)
		? { timeSinceGenerationMs: Math.max(0, Date.now() - createdAt) }
		: {};
};

const getSuggestionRemixAttributes = (
	suggestion: Pick<Suggestion, 'action' | 'actionKind'>,
): { remixSubtype?: string; remixType?: string } => {
	if (suggestion.actionKind !== 'SUGGESTED_REMIX') {
		return {};
	}
	return {
		...(suggestion.action?.launchOptions?.dynamicUiType
			? { remixType: suggestion.action.launchOptions.dynamicUiType }
			: {}),
		...(suggestion.action?.launchOptions?.dynamicUiSubtype
			? { remixSubtype: suggestion.action.launchOptions.dynamicUiSubtype }
			: {}),
	};
};

const getAffectedBlocks = (suggestion: SuggestionAnalyticsSnapshot): number => {
	const localIds = [
		...(suggestion.action?.localIds ?? []),
		...(suggestion.toolCalls?.flatMap((toolCall) =>
			toolCall.name === 'removeNodes'
				? (toolCall.arguments.localIds ?? [])
				: toolCall.arguments.localId
					? [toolCall.arguments.localId]
					: [],
		) ?? []),
	];

	return new Set(localIds).size;
};

export const useEmptyStateExposedAnalytics = ({
	emptyStateType,
	enabled,
	fireAnalyticsEvent,
}: {
	emptyStateType: AiSuggestionsEmptyStateType;
	enabled: boolean;
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent;
}): void => {
	const lastExposedEmptyStateRef = useRef<AiSuggestionsEmptyStateType | null>(null);

	useEffect(() => {
		if (!enabled) {
			lastExposedEmptyStateRef.current = null;
			return;
		}
		if (!fireAnalyticsEvent || lastExposedEmptyStateRef.current === emptyStateType) {
			return;
		}

		fireAnalyticsEvent({
			action: ACTION.EMPTY_STATE_EXPOSED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: { emptyStateType },
		});
		lastExposedEmptyStateRef.current = emptyStateType;
	}, [emptyStateType, enabled, fireAnalyticsEvent]);
};

export const useReasoningViewedAnalytics = (
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent,
): ((suggestion: Suggestion) => void) => {
	return useCallback(
		(suggestion: Suggestion) => {
			fireAnalyticsEvent?.({
				action: ACTION.REASONING_VIEWED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
					affectedBlocks: getAffectedBlocks(suggestion),
					agentId: suggestion.createdByAgent?.accountId,
					hasSources: Boolean(suggestion.sources?.length),
					interactionPoint: 'sidebar',
					reasoningCharacterCount: suggestion.reasoning?.length ?? 0,
					...getTimeSinceGenerationAttribute(suggestion),
					...getSuggestionRemixAttributes(suggestion),
					suggestionId: suggestion.id,
					suggestionType: suggestion.type,
				},
			});
		},
		[fireAnalyticsEvent],
	);
};

export const useCancelledAnalytics = (
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent,
): (() => void) => {
	return useCallback(() => {
		fireAnalyticsEvent?.({
			action: ACTION.CANCELLED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: { interactionPoint: 'sidebar' },
		});
	}, [fireAnalyticsEvent]);
};

export const useSuggestionsPanelOpenAnalytics = ({
	entryPoint,
	fireAnalyticsEvent,
	isFullPanelLoading,
	suggestions,
}: {
	entryPoint: AiSuggestionsRightRailEntryPoint;
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent;
	isFullPanelLoading: boolean;
	suggestions?: readonly SuggestionAnalyticsSnapshot[];
}): void => {
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current || isFullPanelLoading || !fireAnalyticsEvent) {
			return;
		}

		const suggestionDetails = (suggestions ?? []).map((suggestion) => ({
			suggestionType: suggestion.type,
			actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
			affectedBlocks: getAffectedBlocks(suggestion),
			...getTimeSinceGenerationAttribute(suggestion),
			...getSuggestionRemixAttributes(suggestion),
		}));
		fireAnalyticsEvent({
			action: ACTION.RIGHT_RAIL_VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				entryPoint,
				numberOfSuggestions: suggestionDetails.length,
				suggestionDetails,
			},
		});
		hasFiredRef.current = true;
	}, [entryPoint, fireAnalyticsEvent, isFullPanelLoading, suggestions]);
};
